// Strategy + Registry (ADR-0008) for authentication. Each strategy module
// self-registers on import; auth/index.ts imports every strategy once.
// Adding a strategy = one new file + one import in auth/index.ts.

import type { ErpMode } from '../env';
import type { AuthStrategy, AuthStrategyName } from './types';

type Factory = () => AuthStrategy;

const factories = new Map<string, Factory>();
const instances = new Map<string, AuthStrategy>();

export function registerStrategy(name: AuthStrategyName, factory: Factory): void {
  if (!name) throw new Error('auth: empty strategy name');
  if (typeof factory !== 'function') throw new Error(`auth: nil factory for ${name}`);
  if (factories.has(name)) throw new Error(`auth: strategy ${name} registered twice`);
  factories.set(name, factory);
}

export function getStrategy(name: AuthStrategyName): AuthStrategy {
  const existing = instances.get(name);
  if (existing) return existing;
  const f = factories.get(name);
  if (!f) throw new Error(`auth: unknown strategy ${name}`);
  const s = f();
  instances.set(name, s);
  return s;
}

export function registeredStrategies(): string[] {
  return [...factories.keys()].sort();
}

/**
 * Which strategy a deployment uses. Node mode always authenticates against the
 * node (no internet). Online uses Authentik — except under the mock
 * interceptor, which can only fake plain JSON endpoints, so mocks use 'local'.
 */
export function strategyNameFor(mode: ErpMode, useMocks: boolean): AuthStrategyName {
  if (mode === 'node' || useMocks) return 'local';
  return 'authentik';
}

/** Test hook. */
export function __resetStrategyInstances(): void {
  instances.clear();
}
